import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  EvidenceListResponse,
  EvidenceDetailResponse,
  EvidenceType,
  EvidenceStatus,
} from '@stfors/shared-types';
import { api } from '../client';

export interface IntakeEvidenceInput {
  project_id: number;
  evidence_type: EvidenceType;
  description: string;
  serial_number?: string;
  make_model?: string;
  storage_physical?: string;
  storage_digital?: string;
  owner_name?: string;
  client_name?: string;
  collected_from?: string;
  acquired_date: number; // epoch ms
}

export function useEvidenceList(projectId?: number) {
  const qs = projectId !== undefined ? `?project_id=${projectId}` : '';
  return useQuery({
    queryKey: ['evidence', { project_id: projectId ?? null }],
    queryFn: ({ signal }) =>
      api.get<EvidenceListResponse>(`/api/evidence${qs}`, signal),
  });
}

export function useEvidenceDetail(id: number | null) {
  return useQuery({
    queryKey: ['evidence', { id }],
    queryFn: ({ signal }) =>
      api.get<EvidenceDetailResponse>(`/api/evidence/${id}`, signal),
    enabled: id !== null,
  });
}

export function useIntakeEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: IntakeEvidenceInput) =>
      api.post<{ evidence: unknown }>('/api/evidence', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['evidence'] });
    },
  });
}

export interface UpdateEvidenceInput {
  id: number;
  evidence_type?: EvidenceType;
  description?: string;
  serial_number?: string | null;
  make_model?: string | null;
  storage_physical?: string | null;
  storage_digital?: string | null;
  owner_name?: string | null;
  client_name?: string | null;
  collected_from?: string | null;
  acquired_date?: number;
  evidence_status?: EvidenceStatus;
  disposition?: string | null;
  /** Overwriting/clearing an existing hash requires the evidence.delete
   *  (admin) permission server-side; setting a first-time hash is allowed
   *  with evidence.write. */
  hash_value?: string | null;
}

// Edit an evidence ENTRY's own fields (correction). The append-only
// timeline is untouched; the API records the change in the audit log and
// appends a `note` event summarizing it.
export function useUpdateEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateEvidenceInput) =>
      api.patch<EvidenceDetailResponse>(`/api/evidence/${id}`, patch),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['evidence', { id: vars.id }] });
      void qc.invalidateQueries({ queryKey: ['evidence'] });
    },
  });
}

export type EvidenceEventType =
  | 'received' | 'transferred' | 'imaged' | 'analyzed'
  | 'photographed' | 'released' | 'destroyed' | 'note';

export interface AppendEventInput {
  evidence_id: number;
  event_type: EvidenceEventType;
  description?: string;
  from_location?: string;
  to_location?: string;
  hash_value?: string;
}

export function useAppendEvidenceEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ evidence_id, ...body }: AppendEventInput) =>
      api.post<{ event: unknown; evidence: unknown }>(
        `/api/evidence/${evidence_id}/events`,
        body,
      ),
    onSuccess: (_data, vars) => {
      // Invalidate both the detail (timeline + hash on header) and any
      // list queries that show evidence rows.
      void qc.invalidateQueries({ queryKey: ['evidence', { id: vars.evidence_id }] });
      void qc.invalidateQueries({ queryKey: ['evidence'] });
    },
  });
}

export interface UpdateEvidenceEventInput {
  evidence_id: number;
  event_id: number;
  event_type?: EvidenceEventType;
  event_at?: number;
  from_location?: string | null;
  to_location?: string | null;
  description?: string | null;
}

// Correct a mistake on an already-logged chain-of-custody event. The event log
// is append-only by default; the API edits it only through an audited override
// (migration 0050) and records the change in the audit log.
export function useUpdateEvidenceEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ evidence_id, event_id, ...patch }: UpdateEvidenceEventInput) =>
      api.patch<EvidenceDetailResponse>(
        `/api/evidence/${evidence_id}/events/${event_id}`,
        patch,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['evidence', { id: vars.evidence_id }] });
      void qc.invalidateQueries({ queryKey: ['evidence'] });
    },
  });
}
